import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
} from '@angular/router';

import { AuthService } from './auth.service';
import { BaseHttpService } from '../shared/services/basehttp.service';
import { Constants } from '../shared/constants';
import { RouteRoleConfig } from 'src/app/configuration/roleconfiguration';
import { SiteminderAuthService } from './oidc-config.service';
import { StorageService } from '../shared/services/token-storage.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  constant: Constants = new Constants();

  private readonly LOGOUT_FLAG_KEY =
    'siteminder_force_logout';

  constructor(
    public auth: AuthService,
    public router: Router,
    private siteminderAuthService: SiteminderAuthService,
    private baseHttpService: BaseHttpService,
    private storage: StorageService
  ) {}

  private isLogoutGuardOn(): boolean {
    try {
      const sessionLogoutFlag =
        sessionStorage.getItem(
          this.LOGOUT_FLAG_KEY
        );

      const localLogoutFlag =
        localStorage.getItem(
          this.LOGOUT_FLAG_KEY
        );

      return (
        sessionLogoutFlag === '1' ||
        localLogoutFlag === '1'
      );
    } catch (error) {
      this.logError(
        'Logout guard check failed',
        error
      );

      return false;
    }
  }

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean> {
    try {
      if (this.isLogoutGuardOn()) {
        try {
          await this.router.navigate(
            ['/login'],
            {
              queryParams: {
                loggedout: '1',
              },
            }
          );
        } catch (error) {
          this.logError(
            'Logout redirect failed',
            error
          );
        }

        return false;
      }

      let isAuthenticated = false;

      try {
        isAuthenticated =
          await this.siteminderAuthService.isLoggedIn();
      } catch (error) {
        this.logError(
          'SiteMinder authentication check failed',
          error
        );

        isAuthenticated = false;
      }

      if (!isAuthenticated) {
        try {
          await this.router.navigate([
            '/login',
          ]);
        } catch (error) {
          this.logError(
            'Login redirect failed',
            error
          );
        }

        return false;
      }

      const currentRouteUrl =
        state.url || '';

      const allowedRoles =
        this.getAllowedRolesForRoute(
          currentRouteUrl
        );

      if (
        allowedRoles &&
        allowedRoles.length > 0
      ) {
        const hasAccess =
          await this.checkUserRoles(
            allowedRoles
          );

        if (!hasAccess) {
          try {
            await this.router.navigate(
              ['/unauthorized'],
              {
                queryParams: {
                  code: '403',
                  message:
                    'Unauthorized Access',
                  helpText:
                    'You do not have permission to view this page.',
                },
              }
            );
          } catch (error) {
            this.logError(
              'Unauthorized redirect failed',
              error
            );
          }

          return false;
        }
      }

      return true;
    } catch (error) {
      this.logError(
        'Route authorization failed',
        error
      );

      try {
        await this.router.navigate([
          '/login',
        ]);
      } catch (navigationError) {
        this.logError(
          'Fallback login redirect failed',
          navigationError
        );
      }

      return false;
    }
  }

  checkUserRoles(
    allowedRoles: string[]
  ): Promise<boolean> {
    return new Promise<boolean>(
      (resolve) => {
        try {
          const userRole =
            this.storage.get(
              'LoggedInUserTypeDesc'
            ) as string;

          if (userRole) {
            const hasAccess =
              allowedRoles.includes(
                userRole
              );

            resolve(hasAccess);
            return;
          }

          resolve(false);
        } catch (error) {
          this.logError(
            'Role authorization check failed',
            error
          );

          resolve(false);
        }
      }
    );
  }

  private getAllowedRolesForRoute(
    routeUrl: string
  ): string[] {
    try {
      const routeConfig =
        RouteRoleConfig.find(
          (config) =>
            config.navigateUrl ===
            routeUrl
        );

      return routeConfig
        ? routeConfig.roles
        : [];
    } catch (error) {
      this.logError(
        'Route role lookup failed',
        error
      );

      return [];
    }
  }

  private logError(
    context: string,
    error: unknown
  ): void {
    try {
      const messages: string[] = [];
      let currentError: unknown =
        error;
      let depth = 0;

      while (
        currentError &&
        depth < 5
      ) {
        if (
          currentError instanceof Error
        ) {
          messages.push(
            currentError.message ||
              currentError.name
          );

          const nestedError =
            currentError as Error & {
              cause?: unknown;
              innerException?: unknown;
              innerError?: unknown;
            };

          currentError =
            nestedError.cause ??
            nestedError.innerException ??
            nestedError.innerError ??
            null;
        } else {
          messages.push(
            'Unknown error'
          );

          currentError = null;
        }

        depth++;
      }

      console.error(
        `${context}${
          messages.length
            ? `: ${messages.join(
                ' | '
              )}`
            : ''
        }`
      );
    } catch {
      console.error(context);
    }
  }
}